/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Text, Box, Button, useMultiStyleConfig} from '@chakra-ui/react'
import {CloseIcon} from '../../../components/icons'
import {REMOVE_FILTER} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {remCalc} from '../../../theme/_utils'

const SelectedRefinements = ({toggleFilter, selectedFilterValues, filters, handleReset}) => {
    const styles = useMultiStyleConfig('Filters', {variant: 'selectedFilterButton'})
    const {formatMessage} = useIntl()
    const priceFilterValues = filters?.find((filter) => filter.attributeId === 'price')

    let selectedFilters = []
    for (const key in selectedFilterValues) {
        const filters = selectedFilterValues[key].split('|')
        filters?.forEach((filter) => {
            const selected = {
                uiLabel:
                    key === 'price'
                        ? priceFilterValues?.values?.find(
                              (priceFilter) => priceFilter.value === filter,
                          )?.label
                        : filter,
                value: key,
                apiLabel: filter,
            }

            if (filter === '(0.01..9999999)') return // We hide the implicit refinement for product visibility

            if (selected.value !== 'htype' && selected.value !== 'cgid') {
                selectedFilters.push(selected)
            }
        })
    }

    return (
        <>
            {selectedFilters.length > 0 && (
                <Box
                    data-testid="sf-selected-refinements"
                    {...styles?.selectedRefinementsContainer}
                >
                    {selectedFilters?.map((filter, idx) => (
                        <Box key={idx} marginLeft={0} marginRight={0} flexShrink={0} height="100%">
                            <Button
                                //{...styles.filterButton}
                                {...styles.selectedRefinementsButton}
                                onClick={() =>
                                    toggleFilter({value: filter.apiLabel}, filter.value, '', true)
                                }
                                aria-label={formatMessage(REMOVE_FILTER, {label: filter.uiLabel})}
                                _focusVisible={{
                                    border: remCalc(3),
                                    borderStyle: 'solid',
                                }}
                            >
                                <Box {...styles.selectedRefinementsIconBox}>
                                    <CloseIcon {...styles.filterIcon} />
                                </Box>
                                <Text {...styles.filterText} alignSelf="stretch">
                                    {filter.uiLabel}
                                </Text>
                            </Button>
                        </Box>
                    ))}

                    {selectedFilters?.length > 0 && (
                        <Box {...styles?.removeAllButtonContainer}>
                            <Button
                                {...styles.filterButton}
                                {...styles.removeAllButton}
                                onClick={handleReset}
                                aria-label={formatMessage({
                                    id: 'selected_refinements.action.assistive_msg.clear_all',
                                    defaultMessage: 'Clear all filters',
                                })}
                                _focusVisible={{
                                    border: 'small',
                                    borderColor: 'button.border.primary-focused',
                                    borderStyle: 'solid',
                                }}
                                onFocus={(e) => {
                                    e.target.scrollIntoView({block: 'nearest', behavior: 'smooth'})
                                }}
                            >
                                <Text textStyle="body-small" fontWeight="normal">
                                    <FormattedMessage
                                        defaultMessage="Remove All"
                                        id="selected_refinements.action.clear_all"
                                    />
                                </Text>
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
        </>
    )
}

SelectedRefinements.propTypes = {
    filters: PropTypes.array,
    selectedFilterValues: PropTypes.object,
    toggleFilter: PropTypes.func,
    handleReset: PropTypes.func,
}

export default SelectedRefinements
